import { Box, Typography } from "@outschool/backpack";
import { Modal } from "@outschool/ui-legacy-component-library";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import _ from "lodash";
import React from "react";

import { FeatureFlag } from "../../../shared/Env";
import { renderCurrencyString } from "../../../shared/Pricing";
import { usePricingOffers } from "../../hooks/CurrentUser";
import { useFeatureFlag } from "../../lib/FeatureFlags";
import HeaderNavItem from "./HeaderNavItem";
import WalletIcon from "./WalletIcon";

type PricingOffer = ReturnType<typeof usePricingOffers>[number];

const PADDING_X = "35px";
const WALLET_COLORS = {
  greenLight: "#E0F0E9",
  yellowLight: "#FEFAEE",
  green: "#5CA87F",
  yellow: "#F6C95B",
  grayLight: "#f8f8f9",
  gray: "#808080",
  grayDark: "#535353"
};

export default function WalletFundsNavItem({
  onClick = () => {},
  isMobile = false
}) {
  const dollarFormatter = (centsRemaining: number) =>
    renderCurrencyString({
      priceInCents: centsRemaining,
      showCurrencyCode: true,
      displayNoCents: true
    });

  const showUnusedWalletFunds = useFeatureFlag(
    FeatureFlag.ShowUnusedWalletFunds
  );
  const [showModal, setShowModal] = React.useState(false);
  const pricingOffers = usePricingOffers({ skip: !showUnusedWalletFunds });
  const centsRemaining = _.sum(
    pricingOffers?.map(pricingOffer => pricingOffer.remainingCapCents)
  );

  // If sum above $500, show $500+ in nav bar
  const centsUpperLimit = 50000;
  const hasLowFunds = centsRemaining < 50;
  const fundsRemaining = dollarFormatter(centsRemaining);

  return showUnusedWalletFunds && pricingOffers?.length ? (
    <HeaderNavItem
      onClick={() => {
        isMobile && onClick();
        setShowModal(true);
      }}
      sx={{
        alignSelf: "center",
        borderRadius: "5px",
        padding: isMobile ? "none" : "tiny",
        marginX: isMobile ? "none" : "small",
        width: isMobile ? "100%" : null,
        backgroundColor: hasLowFunds
          ? WALLET_COLORS.yellowLight
          : WALLET_COLORS.greenLight,
        color: hasLowFunds ? WALLET_COLORS.yellow : WALLET_COLORS.green,
        justifyContent: "center"
      }}
    >
      <WalletFundsModal
        showModal={showModal}
        onClose={() => setShowModal(false)}
        pricingOffers={pricingOffers}
        fundsRemaining={fundsRemaining}
        dollarFormatter={dollarFormatter}
        isMobile={isMobile}
      />
      <WalletIcon showStar={!hasLowFunds} alt="Wallet" />
      {centsRemaining > centsUpperLimit
        ? `${dollarFormatter(centsUpperLimit)}+`
        : fundsRemaining}
    </HeaderNavItem>
  ) : null;
}

function WalletFundsModal({
  showModal,
  onClose,
  pricingOffers,
  fundsRemaining,
  dollarFormatter,
  isMobile
}) {
  return (
    <Modal
      open={showModal}
      onClose={onClose}
      sx={{
        padding: "medium"
      }}
    >
      <Box
        flex
        sx={{
          flexDirection: "row",
          justifyContent: "start",
          paddingY: "0.5em"
        }}
      >
        <WalletIcon size="large" showStar={true} />
        <Box>
          <Typography
            variant="h2"
            sx={{
              color: "common.black"
            }}
          >
            Wallet
          </Typography>
          <Typography
            variant="inherit"
            sx={{
              color: WALLET_COLORS.gray
            }}
          >
            Funds remaining in your wallets.{" "}
          </Typography>
        </Box>
      </Box>
      {pricingOffers.map((pricingOffer: PricingOffer) => (
        <WalletCard
          key={pricingOffer.uid}
          pricingOffer={pricingOffer}
          dollarFormatter={dollarFormatter}
          isMobile={isMobile}
        />
      ))}
      {pricingOffers.length > 1 && (
        <Box
          flex
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            paddingX: PADDING_X,
            paddingTop: "0.5em",
            paddingBottom: "1em",
            color: WALLET_COLORS.grayDark,
            fontSize: isMobile ? "18px" : "24px"
          }}
        >
          <Typography variant="inherit">Total Wallet Balance </Typography>
          <Typography variant="inherit">{fundsRemaining} </Typography>
        </Box>
      )}
    </Modal>
  );
}

function WalletCard({ pricingOffer, dollarFormatter, isMobile }) {
  return (
    <Box
      flex
      sx={{
        backgroundColor: pricingOffer.isFinancialAid
          ? WALLET_COLORS.grayLight
          : pricingOffer.remainingCapCents > 0
          ? WALLET_COLORS.greenLight
          : WALLET_COLORS.yellowLight,
        flexDirection: "column",
        paddingTop: "1em",
        paddingBottom: "2em",
        paddingX: PADDING_X,
        marginY: "1em",
        borderRadius: "4px"
      }}
    >
      <Typography
        variant={isMobile ? "h4" : "h3"}
        sx={{
          marginBottom: "0.5em"
        }}
      >
        {pricingOffer.isFinancialAid
          ? "Financial Assistance"
          : `Covered By ${pricingOffer.buyerOrgName}`}
      </Typography>
      <Box
        flex
        sx={{
          flexDirection: "row",
          justifyContent: "space-between",
          color: WALLET_COLORS.grayDark,
          fontSize: isMobile ? "18px" : "24px"
        }}
      >
        <Typography variant="inherit">Current Wallet Balance</Typography>
        <Typography variant="inherit">
          {dollarFormatter(pricingOffer.remainingCapCents)}
        </Typography>
      </Box>
    </Box>
  );
}
